import { useState, useEffect } from 'react';

import { KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { DragEndEvent } from '@dnd-kit/core/dist/types';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { append, remove } from 'ramda';

import { IBlocksProps } from 'components/shared/ArrayFields/types';

import { ISpoiler } from './Spoiler/types';
import { IUseAccordionProps } from './types';

export function useAccordion({ data: blockData, onUpdate }: IUseAccordionProps) {
  const { spoilers } = blockData.data;
  const [activeItem, setActiveItem] = useState(-1);
  const [spoilersDndIds, setSpoilersDndIds] = useState([]);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const lastSpoilerIndex = spoilers.length || -1;

  const handleToggleSpoiler = (currentIndex: number, newIndex: number) => {
    if (currentIndex > -1 && currentIndex === newIndex) {
      return setActiveItem(-1);
    }
    return setActiveItem(newIndex);
  };

  const handleDeleteSpoiler = (index: number) => {
    const newObject = {
      ...blockData,
      data: {
        spoilers: remove(index, 1, spoilers),
      },
    };
    onUpdate(newObject);
  };

  const handleAddSpoiler = () => {
    const newSpoiler: ISpoiler = {
      title: 'New Section',
      children: [{ data: {}, type: 'text' } as IBlocksProps],
    };

    const newObject = {
      ...blockData,
      data: {
        spoilers: append(newSpoiler, spoilers),
      },
    };

    onUpdate(newObject);
    setActiveItem(lastSpoilerIndex);
  };

  const handleChangeSpoiler = (index: number, data: ISpoiler) => {
    const newSpoilers = [...spoilers];
    newSpoilers.splice(index, 1, data);
    const newObject = {
      ...blockData,
      data: {
        spoilers: newSpoilers,
      },
    };
    onUpdate(newObject);
  };

  const handleSortSpoilers = (spoilersIds: string[]) => {
    const newSpoilers = [...spoilersIds.map((id: any) => spoilers[id])];

    const newObject = {
      ...blockData,
      data: {
        spoilers: newSpoilers,
      },
    };
    onUpdate(newObject);
    setActiveItem(-1);
  };

  function handleDragEnd(event: DragEndEvent) {
    document.body.classList.remove('dragging');
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = spoilersDndIds.indexOf(active.id as never);
      const newIndex = spoilersDndIds.indexOf(over.id as never);
      const newSpoilersDndIds = arrayMove(spoilersDndIds, oldIndex, newIndex);
      setSpoilersDndIds(newSpoilersDndIds);
      return handleSortSpoilers(newSpoilersDndIds as string[]);
    }
  }

  useEffect(() => {
    setSpoilersDndIds(spoilers.map((v: ISpoiler, i: number) => String(i)) as never[]);
  }, [spoilers]);

  return {
    spoilers,
    activeItem,
    handleToggleSpoiler,
    handleDeleteSpoiler,
    handleChangeSpoiler,
    handleAddSpoiler,
    handleSortSpoilers,
    setActiveItem,
    sensors,
    handleDragEnd,
    spoilersDndIds,
  };
}
